<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ApexAreaChart from './Apex/ApexAreaChart.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  value: {
    type: [Number, String],
    default: 0,
  },
  previousValue: {
    type: Number,
    default: null,
  },
  format: {
    type: String,
    default: 'number',
  },
  icon: {
    type: String,
    default: '',
  },
  iconColor: {
    type: String,
    default: 'text-n-blue-11',
  },
  iconBg: {
    type: String,
    default: 'bg-n-blue-3',
  },
  sparklineData: {
    type: Array,
    default: () => [],
  },
  sparklineColor: {
    type: String,
    default: '#3b82f6',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  invertTrend: {
    type: Boolean,
    default: false,
  },
  subtitle: {
    type: String,
    default: '',
  },
});
const { locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));

const displayValue = ref(0);
const isAnimating = ref(false);
const hasLoadedOnce = ref(false);

const formatValue = val => {
  if (typeof val === 'string') return val;

  if (props.format === 'time') {
    const totalSeconds = Math.round(val);
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) {
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  if (props.format === 'percent') {
    return `${Number(val).toFixed(1)}%`;
  }

  return new Intl.NumberFormat(browserLocale.value).format(Math.round(val));
};

const animateValue = (start, end, duration = 800) => {
  if (typeof end !== 'number') {
    displayValue.value = end;
    return;
  }

  isAnimating.value = true;
  const startTime = performance.now();

  const animate = currentTime => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easeOut = 1 - (1 - progress) ** 3;

    displayValue.value = start + (end - start) * easeOut;

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      displayValue.value = end;
      isAnimating.value = false;
    }
  };

  requestAnimationFrame(animate);
};

const trendInfo = computed(() => {
  if (props.previousValue === null || props.previousValue === 0) return null;

  const current = typeof props.value === 'number' ? props.value : 0;
  const diff = ((current - props.previousValue) / props.previousValue) * 100;

  let direction = 'neutral';
  if (diff > 0) direction = 'up';
  else if (diff < 0) direction = 'down';

  if (props.invertTrend) {
    if (direction === 'up') direction = 'down';
    else if (direction === 'down') direction = 'up';
  }

  return {
    direction,
    value: Math.abs(diff).toFixed(1),
    isPositive:
      (direction === 'up' && !props.invertTrend) ||
      (direction === 'down' && props.invertTrend),
  };
});

const trendColor = computed(() => {
  if (!trendInfo.value) return '';
  if (trendInfo.value.isPositive) return props.sparklineColor;
  if (trendInfo.value.direction === 'neutral') return '#94a3b8';
  return '#ef4444';
});

watch(
  () => props.value,
  newVal => {
    const numNew = typeof newVal === 'number' ? newVal : 0;
    if (!hasLoadedOnce.value) {
      hasLoadedOnce.value = true;
      animateValue(0, numNew);
    } else {
      // On subsequent refreshes, update instantly without animation
      displayValue.value = numNew;
    }
  }
);

onMounted(() => {
  const numVal = typeof props.value === 'number' ? props.value : 0;
  if (numVal > 0) {
    hasLoadedOnce.value = true;
  }
  animateValue(0, numVal);
});
</script>

<template>
  <div
    class="group relative flex flex-col p-4 rounded-xl bg-n-solid-2 border border-n-weak hover:border-n-strong/20 transition-all duration-300 hover:shadow-lg hover:shadow-n-alpha-1/5 min-w-0"
  >
    <div
      v-if="loading && !hasLoadedOnce"
      class="absolute inset-0 flex items-center justify-center bg-n-solid-2 rounded-xl z-10"
    >
      <div
        class="w-5 h-5 border-2 border-n-slate-6 border-t-n-blue-9 rounded-full animate-spin"
      />
    </div>

    <div class="flex items-start justify-between gap-3 mb-2">
      <div class="flex items-center gap-2 min-w-0">
        <div
          v-if="icon"
          class="shrink-0 p-2 rounded-lg transition-transform duration-300 group-hover:scale-105"
          :class="iconBg"
        >
          <Icon :icon="icon" class="size-4" :class="iconColor" />
        </div>
        <span class="text-xs font-medium text-n-slate-11 truncate">
          {{ title }}
        </span>
      </div>

      <div
        v-if="trendInfo && trendInfo.direction !== 'neutral'"
        class="shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium"
        :class="{
          'bg-emerald-500/10 text-emerald-600': trendInfo.isPositive,
          'bg-red-500/10 text-red-600': !trendInfo.isPositive,
        }"
      >
        <Icon
          :icon="
            trendInfo.direction === 'up'
              ? 'i-lucide-trending-up'
              : 'i-lucide-trending-down'
          "
          class="size-3"
        />
        <span>{{ trendInfo.value }}%</span>
      </div>
    </div>

    <div class="flex items-end justify-between gap-3">
      <div class="min-w-0">
        <p
          class="text-2xl font-semibold tracking-tight text-n-slate-12 transition-opacity duration-300 truncate"
        >
          {{ formatValue(displayValue) }}
        </p>
        <p v-if="subtitle" class="text-[10px] text-n-slate-10 mt-0.5 truncate">
          {{ subtitle }}
        </p>
      </div>

      <div v-if="sparklineData.length > 1" class="shrink-0 w-20 h-10">
        <ApexAreaChart
          :data="sparklineData"
          :color="trendColor || sparklineColor"
          :height="40"
          width="100%"
          :stroke-width="1.5"
        />
      </div>
    </div>
  </div>
</template>
